package es.subastia.auctiondetail

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Description
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.filled.FavoriteBorder
import androidx.compose.material.icons.filled.Gavel
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.LocationOn
import androidx.compose.material.icons.filled.Schedule
import androidx.compose.material.icons.filled.Share
import androidx.compose.material.icons.filled.Verified
import androidx.compose.material.icons.filled.Work
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SuggestionChip
import androidx.compose.material3.SuggestionChipDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import coil3.compose.AsyncImage
import es.subastia.domain.model.AuctionProcedureType
import es.subastia.navigation.NavRoute
import es.subastia.navigation.Navigator
import es.subastia.ui.components.SubastiaButton
import es.subastia.ui.model.AuctionDatesUiModel
import es.subastia.ui.model.AuctionUiModel
import es.subastia.ui.model.LotUiModel
import es.subastia.utils.formatMoney
import es.subastia.utils.rememberShareAction
import es.subastia.utils.toUserFriendlyDate
import org.jetbrains.compose.resources.stringResource
import org.jetbrains.compose.ui.tooling.preview.Preview
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import subastia.composeapp.generated.resources.Res
import subastia.composeapp.generated.resources.btn_back
import subastia.composeapp.generated.resources.btn_share
import subastia.composeapp.generated.resources.btn_view_boe
import subastia.composeapp.generated.resources.desc_ai_verified
import subastia.composeapp.generated.resources.desc_map_location
import subastia.composeapp.generated.resources.desc_toggle_favorite
import subastia.composeapp.generated.resources.icon_auction_id
import subastia.composeapp.generated.resources.icon_boe_id
import subastia.composeapp.generated.resources.icon_catastral
import subastia.composeapp.generated.resources.icon_email
import subastia.composeapp.generated.resources.icon_end
import subastia.composeapp.generated.resources.icon_expediente
import subastia.composeapp.generated.resources.icon_location
import subastia.composeapp.generated.resources.icon_name
import subastia.composeapp.generated.resources.icon_phone
import subastia.composeapp.generated.resources.icon_start
import subastia.composeapp.generated.resources.image_placeholder_text
import subastia.composeapp.generated.resources.label_additional
import subastia.composeapp.generated.resources.label_appraisal
import subastia.composeapp.generated.resources.label_auction_value
import subastia.composeapp.generated.resources.label_charges
import subastia.composeapp.generated.resources.label_claimed_amount
import subastia.composeapp.generated.resources.label_current_bid
import subastia.composeapp.generated.resources.label_deposit
import subastia.composeapp.generated.resources.label_lots_count
import subastia.composeapp.generated.resources.label_min_bid
import subastia.composeapp.generated.resources.label_no_min_bid
import subastia.composeapp.generated.resources.label_possession
import subastia.composeapp.generated.resources.label_registry
import subastia.composeapp.generated.resources.label_visitable
import subastia.composeapp.generated.resources.section_asset_details
import subastia.composeapp.generated.resources.section_auction_data
import subastia.composeapp.generated.resources.section_authority
import subastia.composeapp.generated.resources.title_auction_detail

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AuctionDetailScreen(
    navigator: Navigator,
    auctionId: String,
) {
    val viewModel: AuctionDetailViewModel = koinViewModel(key = auctionId, parameters = { parametersOf(auctionId) })
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val shareAction = rememberShareAction()

    Scaffold(
        containerColor = MaterialTheme.colorScheme.background, // Ensure solid background
        topBar = {
            TopAppBar(
                title = { Text(stringResource(Res.string.title_auction_detail)) },
                navigationIcon = {
                    IconButton(onClick = { navigator.pop() }) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = stringResource(Res.string.btn_back))
                    }
                },
                actions = {
                    if (state is AuctionDetailUiState.Success) {
                        val successState = state as AuctionDetailUiState.Success
                        val auction = successState.auction
                        // Only show share button if feature is available (Mobile)
                        shareAction?.let { action ->
                            IconButton(onClick = {
                                val url = auction.portalUrl
                                action(url)
                            }) {
                                Icon(Icons.Default.Share, contentDescription = stringResource(Res.string.btn_share))
                            }
                        }
                        if (successState.isLoggedIn) {
                            IconButton(onClick = { viewModel.toggleFavorite() }) {
                                Icon(
                                    imageVector = if (successState.isFavorite) Icons.Default.Favorite else Icons.Default.FavoriteBorder,
                                    contentDescription = stringResource(Res.string.desc_toggle_favorite),
                                    tint = if (successState.isFavorite) Color(0xFF4CAF50) else MaterialTheme.colorScheme.onSurface,
                                )
                            }
                        }
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.surface,
                    titleContentColor = MaterialTheme.colorScheme.onSurface,
                ),
            )
        },
    ) { padding ->
        Box(Modifier.padding(padding).fillMaxSize()) {
            when (val stateValue = state) {
                is AuctionDetailUiState.Loading -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
                    }
                }

                is AuctionDetailUiState.Error -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(stringResource(stateValue.error), color = MaterialTheme.colorScheme.error, modifier = Modifier.padding(20.dp))
                    }
                }

                is AuctionDetailUiState.Success -> {
                    AuctionDetailContentAdaptive(stateValue.auction, onImageClick = { url ->
                        navigator.navigateTo(NavRoute.Gallery(url))
                    })
                }
            }
        }
    }
}

@Composable
private fun AuctionDetailContentAdaptive(auction: AuctionUiModel, onImageClick: (String) -> Unit) {
    BoxWithConstraints(Modifier.fillMaxSize()) {
        val isWideScreen = maxWidth > 800.dp

        Row(Modifier.fillMaxSize()) {
            if (isWideScreen) {
                // DESKTOP / TABLET LAYOUT
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .verticalScroll(rememberScrollState())
                        .padding(16.dp),
                ) {
                    AuctionHeroSection(auction, onImageClick)
                    Spacer(Modifier.height(24.dp))
                    AuctionActions(auction)
                }

                Column(
                    modifier = Modifier
                        .weight(1.5f)
                        .verticalScroll(rememberScrollState())
                        .padding(16.dp),
                ) {
                    AuctionInfoGrid(auction)
                }
            } else {
                // MOBILE LAYOUT
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .verticalScroll(rememberScrollState())
                        .padding(bottom = 16.dp), // Only bottom padding
                ) {
                    Box(Modifier.padding(16.dp)) {
                        AuctionHeroSection(auction, onImageClick)
                    }
                    Spacer(Modifier.height(8.dp))
                    AuctionInfoGrid(auction)
                    Spacer(Modifier.height(32.dp))
                    Box(Modifier.padding(horizontal = 16.dp)) {
                        AuctionActions(auction)
                    }
                }
            }
        }
    }
}

@Composable
fun AuctionHeroSection(auction: AuctionUiModel, onImageClick: (String) -> Unit) {
    Column(Modifier.fillMaxWidth()) {
        // 1. Image Card
        Card(
            shape = RoundedCornerShape(24.dp),
            elevation = CardDefaults.cardElevation(defaultElevation = 8.dp),
            modifier = Modifier
                .fillMaxWidth()
                .aspectRatio(1.6f)
                .clickable { auction.imageUrl?.let { onImageClick(it) } },
        ) {
            Box(Modifier.fillMaxSize()) {
                if (!auction.imageUrl.isNullOrBlank()) {
                    AsyncImage(
                        model = auction.imageUrl,
                        contentDescription = auction.title,
                        contentScale = ContentScale.Crop,
                        modifier = Modifier.fillMaxSize(),
                    )
                } else {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(stringResource(Res.string.image_placeholder_text), color = Color.Gray)
                    }
                }

                // Status Badge (Keep inside image)
                auction.status?.let {
                    Surface(
                        modifier = Modifier.align(Alignment.TopEnd).padding(16.dp),
                        color = if (it == "ACTIVE") Color(0xFF4CAF50) else Color.Gray,
                        shape = RoundedCornerShape(50),
                        contentColor = Color.White,
                    ) {
                        Text(
                            text = it,
                            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                            style = MaterialTheme.typography.labelSmall,
                            fontWeight = FontWeight.Bold,
                        )
                    }
                }
            }
        }

        // 2. Info Section (Below Image)
        Column(
            Modifier
                .fillMaxWidth()
                .padding(top = 16.dp, start = 8.dp, end = 8.dp),
        ) {
            auction.type?.let {
                SuggestionChip(
                    onClick = {},
                    label = { Text(it.uppercase()) },
                    colors = SuggestionChipDefaults.suggestionChipColors(
                        containerColor = MaterialTheme.colorScheme.primaryContainer,
                        labelColor = MaterialTheme.colorScheme.onPrimaryContainer,
                    ),
                    border = null,
                )
            }
            Spacer(Modifier.height(4.dp))
            Text(
                auction.title,
                style = MaterialTheme.typography.headlineSmall,
                color = MaterialTheme.colorScheme.onBackground,
                fontWeight = FontWeight.Bold,
            )
            auction.city?.let {
                Spacer(Modifier.height(4.dp))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(
                        Icons.Default.LocationOn,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.size(16.dp),
                    )
                    Spacer(Modifier.width(4.dp))
                    Text(it, color = MaterialTheme.colorScheme.onSurfaceVariant, style = MaterialTheme.typography.bodyMedium)
                }
            }
        }
    }
}

@Composable
fun AuctionActions(auction: AuctionUiModel) {
    val uriHandler = LocalUriHandler.current

    Column(Modifier.fillMaxWidth()) {
        SubastiaButton(
            text = stringResource(Res.string.btn_view_boe),
            onClick = { uriHandler.openUri(auction.portalUrl) },
            modifier = Modifier.fillMaxWidth(),
        )
    }
}

@Composable
fun AuctionInfoGrid(auction: AuctionUiModel) {
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {

        // AI Summary if available (Always show)
        auction.aiSummary?.let {
            Card(
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.tertiaryContainer),
                shape = RoundedCornerShape(16.dp),
                modifier = Modifier.padding(horizontal = 16.dp),
            ) {
                Row(Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                    Icon(
                        Icons.Default.Verified,
                        contentDescription = stringResource(Res.string.desc_ai_verified),
                        tint = MaterialTheme.colorScheme.onTertiaryContainer,
                    )
                    Spacer(Modifier.width(16.dp))
                    Text(it, style = MaterialTheme.typography.bodyLarge, color = MaterialTheme.colorScheme.onTertiaryContainer)
                }
            }
        }

        if (auction.lots.isNotEmpty()) {
            if (auction.lots.size > 1) {
                // MULTI-LOT CAROUSEL
                Text(
                    text = stringResource(Res.string.label_lots_count, auction.lots.size),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.padding(horizontal = 24.dp), // Text indented
                )

                val pagerState = rememberPagerState(pageCount = { auction.lots.size })

                HorizontalPager(
                    state = pagerState,
                    contentPadding = PaddingValues(horizontal = 16.dp), // Start/End visible padding
                    pageSpacing = 8.dp,
                    verticalAlignment = Alignment.Top,
                    modifier = Modifier.fillMaxWidth(),
                ) { page ->
                    val lot = auction.lots[page]
                    LotDetailCard(
                        lot = lot,
                        number = page + 1,
                        total = auction.lots.size,
                        currentPrice = null,
                        globalDescription = null,
                        claimedAmount = auction.claimedAmount,
                    )
                }
            } else {
                // SINGLE LOT VIEW (No pager, just the card)
                val lot = auction.lots.first()
                Box(Modifier.padding(horizontal = 16.dp)) {
                    LotDetailCard(
                        lot = lot,
                        number = 1,
                        total = 1,
                        currentPrice = auction.currentPrice,
                        globalDescription = auction.description,
                        claimedAmount = auction.claimedAmount,
                    )
                }
            }
        }

        // Common Details Section
        Box(Modifier.padding(horizontal = 16.dp)) {
            DetailSection(stringResource(Res.string.section_auction_data)) {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {

                    auction.dates.startDate?.let {
                        DetailRowIcon(Icons.Default.Schedule, stringResource(Res.string.icon_start), it.toUserFriendlyDate())
                    }
                    auction.dates.endDate?.let {
                        DetailRowIcon(Icons.Default.Schedule, stringResource(Res.string.icon_end), it.toUserFriendlyDate())
                    }

                    DetailRowIcon(Icons.Default.Gavel, stringResource(Res.string.icon_expediente), auction.accountExpediente ?: "-")

                    DetailRowIcon(Icons.Default.Info, stringResource(Res.string.icon_auction_id), auction.auctionId)
                    DetailRowIcon(Icons.Default.Verified, stringResource(Res.string.icon_boe_id), auction.boeId ?: "-")
                }
            }
        }

        // Authority Section
        auction.authority?.let { auth ->
            Box(Modifier.padding(horizontal = 16.dp)) {
                DetailSection(stringResource(Res.string.section_authority)) {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        DetailRowIcon(Icons.Default.Work, stringResource(Res.string.icon_name), auth.name ?: "-")
                        auth.email?.let { DetailRowIcon(Icons.Default.Info, stringResource(Res.string.icon_email), it) }
                        auth.phone?.let { DetailRowIcon(Icons.Default.Info, stringResource(Res.string.icon_phone), it) }
                    }
                }
            }
        }
    }
}

@Composable
fun LotDetailCard(
    lot: LotUiModel,
    number: Int,
    total: Int,
    currentPrice: Double? = null,
    globalDescription: String? = null,
    claimedAmount: Double? = null,
) {
    val uriHandler = LocalUriHandler.current

    Card(
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f)),
        border = BorderStroke(5.dp, MaterialTheme.colorScheme.outlineVariant),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Column(Modifier.padding(16.dp)) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = if (total > 1) {
                        "${stringResource(Res.string.section_asset_details)} $number / $total"
                    } else {
                        stringResource(Res.string.section_asset_details)
                    },
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.primary,
                )
            }

            Spacer(Modifier.height(16.dp))

            // Lot Financials
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                if (currentPrice != null) {
                    PriceCard(stringResource(Res.string.label_current_bid), currentPrice, highlighted = true, modifier = Modifier.weight(1f))
                    PriceCard(stringResource(Res.string.label_appraisal), lot.appraisalValue, modifier = Modifier.weight(1f))
                } else {
                    PriceCard(stringResource(Res.string.label_appraisal), lot.appraisalValue, modifier = Modifier.weight(1f))
                    PriceCard(stringResource(Res.string.label_auction_value), lot.auctionValue, highlighted = true, modifier = Modifier.weight(1f))
                }
            }
            // Claimed Amount Row (if exists)
            (claimedAmount ?: 0.0).takeIf { it > 0 }?.let {
                Spacer(Modifier.height(8.dp))
                Row(Modifier.fillMaxWidth()) {
                    InfoCard(stringResource(Res.string.label_claimed_amount), "${it.formatMoney()} €", modifier = Modifier.weight(1f))
                }
            }

            Spacer(Modifier.height(8.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                InfoCard(stringResource(Res.string.label_deposit), "${lot.depositAmount?.formatMoney() ?: "-"} €", modifier = Modifier.weight(1f))
                val minBidValue = if (lot.minBid == 0.0) stringResource(Res.string.label_no_min_bid) else "${lot.minBid?.formatMoney() ?: "-"} €"
                InfoCard(stringResource(Res.string.label_min_bid), minBidValue, modifier = Modifier.weight(1f))
            }

            Spacer(Modifier.height(16.dp))

            val description = lot.description?.takeIf { it.isNotBlank() } ?: globalDescription
            description?.let {
                Text(it, style = MaterialTheme.typography.bodyMedium)
                Spacer(Modifier.height(16.dp))
            }

            val lotLocation = listOfNotNull(lot.address, lot.city, lot.province).filter { it.isNotBlank() }.joinToString(", ")
            if (lotLocation.isNotBlank()) {
                DetailRowIcon(Icons.Default.LocationOn, stringResource(Res.string.icon_location), lotLocation)
                Spacer(Modifier.height(8.dp))
            }

            // MAP SECTION
            if (lot.mapImageUrl != null && lot.latitude != null && lot.longitude != null) {
                Spacer(Modifier.height(8.dp))
                val lat = lot.latitude
                val lng = lot.longitude

                Card(
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth().height(150.dp).clickable {
                        uriHandler.openUri("https://www.google.com/maps/search/?api=1&query=$lat,$lng")
                    },
                ) {
                    AsyncImage(
                        model = lot.mapImageUrl,
                        contentDescription = stringResource(Res.string.desc_map_location),
                        contentScale = ContentScale.Crop,
                        modifier = Modifier.fillMaxSize(),
                    )
                }
                Spacer(Modifier.height(16.dp))
            }

            LotTechnicalDetails(lot)
        }
    }
}

@Composable
fun LotTechnicalDetails(lot: LotUiModel) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        lot.catastralRef?.let { DetailRowIcon(Icons.Default.Description, stringResource(Res.string.icon_catastral), it) }
        lot.visitable?.let { DetailRowIcon(Icons.Default.Info, stringResource(Res.string.label_visitable), it) }
        lot.charges?.let { DetailRowIcon(Icons.Default.Info, stringResource(Res.string.label_charges), it) }
        lot.possessionStatus?.let { DetailRowIcon(Icons.Default.Info, stringResource(Res.string.label_possession), it) }
        lot.registryData?.let { DetailRowIcon(Icons.Default.Description, stringResource(Res.string.label_registry), it) }
        lot.additionalInfo?.let { DetailRowIcon(Icons.Default.Info, stringResource(Res.string.label_additional), it) }
    }
}

@Composable
fun DetailSection(title: String, content: @Composable () -> Unit) {
    Card(
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f)),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Column(Modifier.padding(16.dp)) {
            Text(title, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
            Spacer(Modifier.height(16.dp))
            content()
        }
    }
}

@Composable
fun PriceCard(label: String, amount: Double?, highlighted: Boolean = false, modifier: Modifier = Modifier) {
    Card(
        colors = CardDefaults.cardColors(
            containerColor = if (highlighted) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surface,
        ),
        shape = RoundedCornerShape(12.dp),
        modifier = modifier,
    ) {
        Column(Modifier.padding(12.dp)) {
            Text(label, style = MaterialTheme.typography.labelMedium)
            Text(
                "${amount?.formatMoney() ?: "0"} €",
                style = MaterialTheme.typography.headlineSmall,
                fontWeight = FontWeight.Bold,
                color = if (highlighted) MaterialTheme.colorScheme.onPrimaryContainer else MaterialTheme.colorScheme.onSurface,
            )
        }
    }
}

@Composable
fun InfoCard(label: String, value: String, modifier: Modifier = Modifier) {
    Card(
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        shape = RoundedCornerShape(12.dp),
        modifier = modifier,
    ) {
        Column(Modifier.padding(12.dp)) {
            Text(label, style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
            Text(value, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.SemiBold)
        }
    }
}

@Composable
fun DetailRowIcon(icon: ImageVector, label: String, value: String) {
    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
        Icon(icon, contentDescription = null, tint = MaterialTheme.colorScheme.primary, modifier = Modifier.size(20.dp))
        Spacer(Modifier.width(12.dp))
        Column {
            Text(label, style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            Text(value, style = MaterialTheme.typography.bodyMedium)
        }
    }
}

private val SampleDetailAuction = AuctionUiModel(
    auctionId = "DETAIL-123",
    title = "Chalet de Lujo en Marbella",
    currentPrice = 450000.0,
    auctionValue = 600000.0,
    appraisalValue = 650000.0,
    depositAmount = 30000.0,
    claimedAmount = 100000.0,
    bidStep = 5000.0,
    dates = AuctionDatesUiModel("2024-01-01", "2024-02-01"),
    city = "Marbella",
    province = "Málaga",
    status = "ACTIVE",
    type = "Inmuebles",
    hasLots = false,
    address = "Av. del Mar 10",
    model = null,
    accountExpediente = "EXP-2024-001",
    catastralRef = "9876543210ABC",
    boeId = "BOE-B-2024-123456",
    aiSummary = "Oportunidad de inversión. Precio 30% por debajo de mercado.",
    description = "Espectacular chalet independiente con piscina y jardín privado. Situado en una de las mejores zonas de Marbella, " +
        "con vistas al mar y montaña. Consta de 4 habitaciones, 3 baños y amplio salón comedor.",
    lots = listOf(
        LotUiModel(
            assetId = "ASSET-1",
            visitable = "Sí, concitando cita",
            charges = "Libre de cargas",
            possessionStatus = "Ocupado por ejecutado",
            registryData = "Finca 1234 Registro Marbella 1",
            additionalInfo = "Requiere reforma menor",
            address = "Av. del Mar 10",
            city = "Marbella",
            province = "Málaga",
        ),
    ),
    portalUrl = "https://subastas.boe.es/detalleSubasta.php?idSub=SUB-JA-2025-249439",
    procedureType = AuctionProcedureType.JUDICIAL_APREMIO,
)

@Preview(heightDp = 1600)
@Composable
private fun AuctionDetailScreenPreview() {
    MaterialTheme {
        AuctionDetailContentAdaptive(auction = SampleDetailAuction, onImageClick = {})
    }
}
